/**
 * Legacy Sendbird v3 chat sagas.
 *
 * Restored from git (c5c927eeb^). Import paths updated to reference
 * legacy/ for v3-only code and parent chat/ for shared code.
 */
import {
  sbChannelList,
  sbGetInstance,
  sbGetMessagesContainer,
  sbSendTextMessage,
  sbGetGroupChannel,
  sbGroupChannelExist,
  sbCreateGroupChannelByName,
  sbGetMessageList,
  sbMarkAsReadByURL,
} from 'sendbird-utils';
import { messageReceiveFactory, channelsToThreads } from 'sendbird-utils/lib/imAdapters';
import { select, put, call, all, take, delay, race } from 'redux-saga/effects';

import {
  loadMessage,
  MessageAdapter,
  MessageFactory,
  createChannel,
  channelExists,
  DOCUMENT_CHAT_P2P_TYPE,
  makeChannelName,
  ChatWorkerThreadsAdapter,
  sbInstance,
} from '@/domain/chat/legacy/helpers';
import * as React from 'react';
import { userSelector, userIdSelector } from '@/domain/env';
import Api, { ensure, logoutAction } from '@/domain/api';
import CONSTANTS from '@/domain/constants';
import { toast } from 'react-toastify';
import ChatToast, {
  ChatToastClose,
  ChatToastClasses,
} from '@/components/mui/Layouts/components/Chat/legacy/components/ChatToast';
import { companiesByIdSelector } from '@/domain/companies/companiesSelector';
import { chatParticipantsSelector } from '@/domain/chat/chatSelector';
import { fetchCompanyChatUsers } from '@/domain/chat/chatActions';
import { isMobile } from '@/lib/systemHelpers/browserHelpers';
import { ChatAdapter as SharedChatAdapter } from '@/domain/chat/helpers';
import { notify } from '@/lib/errorLogger';
import * as actions from '@/domain/chat/legacy/chatActions';
import * as selectors from '@/domain/chat/legacy/chatSelector';
import flow from 'lodash/fp/flow';
import partial from 'lodash/fp/partial';

export const getCompanyChatContext = ensure(Api.getCompanyChatContext, fetchCompanyChatUsers, {
  adapter: (resp) => SharedChatAdapter(resp.data),
  tokenType: 'company',
});

function* getMessageQuery(name) {
  const mqs = yield select(selectors.messageQuerySelector);
  return mqs.get(name);
}

function* getChannelHistory(channel, name) {
  const mq = yield call(getMessageQuery, name);
  if (!mq) {
    const messageListQuery = channel.createPreviousMessageListQuery();
    try {
      const messages = yield call(loadMessage, messageListQuery);
      channel.markAsRead();
      yield put({
        type: actions.createChannelAction.success,
        payload: {
          name,
          messages: MessageAdapter(messages),
          channel,
          messageQuery: messageListQuery,
        },
      });
    } catch (err) {
      yield put({
        type: actions.createChannelAction.failure,
        err,
      });
    }
  }
}

export function* ensureCreateCannel({ payload }) {
  const defaults = {
    distinct: false,
    data: null,
    customType: null,
    coverUrlOrImageFile: '',
  };
  const channelOptions = { ...defaults, ...payload };
  const sb = sbGetInstance();
  sbInstance(sb);
  const channels = yield channelExists(sb, payload.name);
  let channel = channels.find((ch) => ch.name === payload.name);
  if (!channel) {
    channel = yield createChannel(
      sb,
      channelOptions.users,
      channelOptions.distinct,
      channelOptions.name,
      channelOptions.coverUrlOrImageFile,
      channelOptions.data,
      channelOptions.customType,
    );
  }
  yield call(getChannelHistory, channel, payload.name);
}

export function* markAsReadChannel({ payload: { name } }) {
  if (name !== null) {
    const channels = yield call(sbGroupChannelExist, name);
    if (Array.isArray(channels) && channels.length > 0) {
      channels[0].markAsRead();
      yield put({
        type: actions.markChannelAsReadAction.type,
        payload: { name },
      });
    }
  }
}

export function threadSwetch(userId) {
  const ENV = process.env.REACT_APP_ENV || 'local';
  return (c, message) => {
    const isNew = typeof message !== 'undefined';
    const isOwnMwssage = isNew && message.sender.userId === userId;
    const { companyId } = JSON.parse((message || c.lastMessage).data);
    const params = c.name.split('#');

    if (ENV === params[0]) {
      if (c.customType === DOCUMENT_CHAT_P2P_TYPE) {
        return {
          type: actions.documentAddThreadAction.type,
          payload: {
            companyId,
            name: c.name,
            message: MessageFactory(message || c.lastMessage),
            unreadMessageCount: c.unreadMessageCount,
            channel: c,
            newMessage: isNew,
            isOwnMwssage,
          },
        };
      }

      return {
        type: actions.receiveMessageAction.success,
        payload: {
          companyId,
          message: MessageFactory(message || c.lastMessage),
          unreadMessageCount: c.unreadMessageCount,
          name: c.name,
          channel: c,
          newMessage: isNew,
          isOwnMwssage,
        },
      };
    }

    return {
      type: '@CHAT/OTHER_ENV',
      payload: params[0],
    };
  };
}

export function* ensureAllChannel() {
  const userId = yield select(userIdSelector);
  const channels = yield sbChannelList();
  const switcher = threadSwetch(userId);
  yield all(
    channels.map((c) => {
      try {
        return put(switcher(c));
      } catch (err) {
        return put({ type: 'CHAT/CHANNEL_PROCESSING_ERROR', payload: { err, c } });
      }
    }),
  );
}

export function ensureChannelExists({ channelName, sb }) {
  const channelListQuery = sb.GroupChannel.createMyGroupChannelListQuery();
  channelListQuery.channelNameContainsFilter = channelName;
  channelListQuery.includeEmpty = true;
  return new Promise((resolve, reject) => {
    if (channelListQuery.hasNext) {
      channelListQuery.next((retrievedChannelList, error) => {
        if (error) {
          reject(error);
          return;
        }
        resolve(retrievedChannelList);
      });
    }
  });
}

export function* markAllDocumentChatsRead({ companyId, documentId }) {
  let participants = yield select(chatParticipantsSelector);
  if (participants.size === 0) {
    yield* getCompanyChatContext();
    participants = yield select(chatParticipantsSelector);
  }

  const cUser = yield select(userSelector);
  yield all(
    participants.toArray().map((participant) =>
      call(markAsReadChannel, {
        payload: {
          name: makeChannelName([participant.userId, cUser.userId], companyId, documentId),
          companyId,
        },
      }),
    ),
  );
}

export function* getDocumentChatNewContext({ companyId, documentId }) {
  let participants = yield select(chatParticipantsSelector);
  if (participants.size === 0) {
    yield* getCompanyChatContext();
    participants = yield select(chatParticipantsSelector);
  }
  const sb = sbGetInstance();
  sbInstance(sb);

  const cUser = yield select(userSelector);
  const channelNames = participants.map((participant) =>
    makeChannelName([participant.userId, cUser.userId], companyId, documentId),
  );

  let channels = [];
  for (let i = 0; i < channelNames.size; i += 1) {
    const ch = yield call(ensureChannelExists, { channelName: channelNames.get(i), sb });
    channels = [...channels, ...ch];
  }
  if (channels instanceof Array && channels.length > 0) {
    for (let i = 0; i < channels.length; i += 1) {
      yield call(getChannelHistory, channels[i], channelNames.get(i));
    }
  }
}

const messageReceive = messageReceiveFactory(CONSTANTS.BUILD_ENV);

export function* ensureActiveChannel({ payload }) {
  if (payload.name) {
    const channelUrl = (yield select(selectors.chatThreadSelector)).getIn([payload.name, 'url']);
    const listQuery = (yield select(selectors.messageQuerySelector)).get(payload.name);
    if (!sbGetInstance().currentUser) {
      yield race({
        timeout: delay(5000),
        cancel: take(actions.chatConnectAction.success),
      });
    }
    try {
      if (channelUrl) {
        const { query, messages, channel } = yield call(sbGetMessagesContainer, channelUrl, listQuery);
        channel.markAsRead();
        yield put({
          type: actions.getMessagesAction.success,
          payload: messageReceive(channel, messages),
          query,
        });
      } else {
        const channels = yield call(sbGroupChannelExist, payload.name);
        if (channels.length) {
          const { query, messages, channel } = yield call(sbGetMessagesContainer, channels[0].url);
          channel.markAsRead();
          yield put({
            type: actions.getMessagesAction.success,
            payload: messageReceive(channel, messages),
            query,
          });
        } else {
          yield put({
            type: actions.getMessagesAction.failure,
            payload: payload.name,
          });
        }
      }
    } catch (err) {
      yield put({
        type: actions.getMessagesAction.failure,
        payload: payload.name,
        err,
      });
    }
  }
}

export function* ensureSendMessage({ payload }) {
  const channelUrl = (yield select(selectors.chatThreadSelector)).getIn([payload.threadId, 'url']);
  const data = JSON.stringify(payload.params);
  if (channelUrl) {
    const channel = yield call(sbGetGroupChannel, channelUrl);
    const message = yield call(sbSendTextMessage, channel, payload.message, data);
    yield put({
      type: actions.sendMessageAction.success,
      payload: messageReceive(channel, [message]),
    });
  } else {
    const channel = yield call(sbCreateGroupChannelByName, payload.threadId);
    const message = yield call(sbSendTextMessage, channel, payload.message, data);
    yield put({
      type: actions.sendMessageAction.success,
      payload: messageReceive(channel, [message]),
    });
  }
}

function notifyWithToast(channelName, message, companyName = null) {
  const options = {
    className: ChatToastClasses.wrapper,
    progressClassName: ChatToastClasses.progress,
    type: toast.TYPE.SUCCESS,
    closeButton: <ChatToastClose />,
  };

  if (companyName !== null && !isMobile) {
    toast(<ChatToast message={message} channelName={channelName} companyName={companyName} group />, options);
  } else {
    toast(<ChatToast channelName={channelName} message={message} />, options);
  }
}

export function* receiveMessage({ payload }) {
  const activeChannel = yield select(selectors.activeChannelSelector);
  const userID = yield select(userIdSelector);

  if (activeChannel !== payload.name) {
    const senderID = payload.messages.first().getIn(['sender', 'userId']);

    if (payload.has('documentId')) {
      const companies = yield select(companiesByIdSelector);
      const cname = companies.getIn([payload.companyId, 'cname']);
      if (typeof cname === 'string') {
        if (senderID !== userID) {
          notifyWithToast(payload.name, payload.messages.first(), cname);
        }
      } else {
        notify.captureEvent(
          new Error("Company have not exists in company's list"),
          {
            data: {
              channel: payload,
              companies: companies.reduce((a, v) => ({ ...a, id: v.id, cname: v.cname }), {}),
            },
          },
          'CHAT_WRONG_COMPANY',
        );
      }
    } else if (senderID !== userID) {
      notifyWithToast(payload.name, payload.messages.first());
    }
  }
}

export function* ensureGetPreviousMessage({ payload }) {
  const query = yield call(getMessageQuery, payload.name);
  const channelUrl = (yield select(selectors.chatThreadSelector)).getIn([payload.name, 'url']);
  if (channelUrl && query && query.hasMore && !query.isLoading) {
    try {
      const messages = yield call(sbGetMessageList, query);
      const channel = yield call(sbGetGroupChannel, channelUrl);
      yield put({
        type: actions.getPreviousMessageAction.success,
        payload: messageReceive(channel, messages),
        query,
      });
    } catch (err) {
      yield put({
        type: actions.getPreviousMessageAction.failure,
        err,
      });
    }
  }
}

export function* ensureTerminateChannel({ documentId, companyId }) {
  const thread = (yield select(selectors.chatThreadSelector))
    .toList()
    .filter((f) => f.companyId === companyId && f.documentId === documentId);
  try {
    yield all(thread.reduce((a, v) => a.push(sbMarkAsReadByURL(v.url)), []));
    yield put({
      type: actions.terminateChannelsAction.success,
      payload: thread.reduce((a, v) => a.concat(v.name), []),
    });
  } catch (err) {
    console.warn(err);
  }
}

export function* ensureAllChatChannel() {
  const params = {};

  // eslint-disable-next-line require-yield
  function* prepare() {
    return params;
  }
  return {
    apiName: 'getChatMessages',
    config: {
      params: yield call(prepare),
    },
    interval: 30000,
    prepare,
    success(data) {
      return {
        type: actions.chatGetWorkerThreadsAction.success,
        payload: flow(ChatWorkerThreadsAdapter, partial(channelsToThreads, [CONSTANTS.BUILD_ENV]))(data),
      };
    },
    failure(err) {
      return logoutAction(actions.chatGetThreadsAction, err);
    },
  };
}

export { actions, selectors };
